'use client';
import type { ReactNode} from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type TagManager from 'react-gtm-module';

import Cookies from 'js-cookie';

import { GTM } from '@shared/config/gtm';

import { createSafeContext, useSafeContext } from '@utilities/context';

interface GoogleTagManagerProviderProps {
  children?: ReactNode;
}

interface ContextValue {
  tagManager?: typeof TagManager;
  triggerAuthEvent: (userId: number, params: { isNewUser?: boolean }) => void;
  sendTagManagerBannerClick: (bannerId: string, url?: string) => void;
}

const Context = createSafeContext<ContextValue>();


export const useTagManager = () => useSafeContext(Context);

const setLastStoredCampaign = (campaign: string) => {
  return Cookies.set('lastCampaign', campaign, { expires: 365 });
};

const getLastStoredCampaign = () => {
  return Cookies.get('lastCampaign');
};

const GoogleTagManagerProvider = ({ children }: GoogleTagManagerProviderProps) => {
  const [tagManagerInstance, setTagManagerInstance] = useState<typeof TagManager>();
  const pendingAuthEvents = useRef<Array<Parameters<typeof triggerAuthEvent>>>([]);
  const pendingTagManagerBannerClicks = useRef<Array<Parameters<typeof sendTagManagerBannerClick>>>([]);

  useEffect(() => {
    import('react-gtm-module').then((m) => {
      const searchParams = new URLSearchParams(window.location.search);
      const campaignName = searchParams.get('utm_campaign') || searchParams.get('campaign');

      if (campaignName) {
        setLastStoredCampaign(campaignName);
      }

      if (GTM.gtmId) {
        m.default.initialize({
          gtmId: GTM.gtmId,
        });
      }

      setTagManagerInstance(m.default);
    });
  }, []);

  const sendTagManagerBannerClick = useMemo(() => {
    if (tagManagerInstance) {
      return (bannerId: string, url?: string) => {
        tagManagerInstance.dataLayer({
          dataLayer: {
            event: 'gtm.bannerClick',
            banner: bannerId,
            urlFrom: url || window.location.href,
          },
        });
      }
    } else {
      return (bannerId: string, url?: string) => {
        pendingTagManagerBannerClicks.current.push([bannerId, url]);
      }
    }
  }, [tagManagerInstance]);

  const triggerAuthEvent: ContextValue['triggerAuthEvent'] = useMemo(() => {
    if (!tagManagerInstance) {
      return (userId, { isNewUser }) => {
        pendingAuthEvents.current.push([userId, { isNewUser }]);
      }
    } else {
      const triggerAuthEventImpl: ContextValue['triggerAuthEvent'] = (userId, { isNewUser }) => {
        const campaignName = getLastStoredCampaign();
        let eventName = null;

        if (isNewUser) {
          eventName = campaignName ? 'gtm.createAccountFromCampaign' : 'gtm.createAccount';
        } else if (campaignName) {
          eventName = 'gtm.loginFromCampaign';
        }

        if (eventName) {
          tagManagerInstance.dataLayer({
            dataLayer: {
              event: eventName,
              url: window.location.href,
              userId: userId,
              // NOTE: do not change name of the field it can affect google analytics
              ...(campaignName
                ? {
                  campaignName,
                }
                : {}),
            },
          });

          setLastStoredCampaign('');
        }
      }

      for (const event of pendingAuthEvents.current) {
        triggerAuthEventImpl(...event);
      }

      return triggerAuthEventImpl;
    }
  },
    [tagManagerInstance],
  );

  const value = useMemo(() => {
    return {
      tagManager: tagManagerInstance,
      triggerAuthEvent,
      sendTagManagerBannerClick
    };
  }, [tagManagerInstance, triggerAuthEvent, sendTagManagerBannerClick]);

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export default GoogleTagManagerProvider;
